/**
 * 딥링크 진입 처리
 * 설계: parnas-server/docs/sms-deeplink-design-2026-08-12.md
 *
 * 문자 → 랜딩(public/open.html) → 커스텀 스킴 → 네이티브 → 여기.
 *
 * 네이티브는 "경로"만 넘기고 로그인 여부는 보지 않는다.
 * 로그인 판단이 이 파일 한 곳에만 있어야 나중에 다른 화면으로 딥링크를 넓힐 때
 * 네이티브를 다시 건드리지 않는다.
 *
 * 진입 방식이 둘인데 처리는 하나로 모인다.
 *   - 콜드스타트 : 네이티브가 최초 로드 URL 자체를 목적지로 준다 → 라우터 가드가 받는다
 *   - 웜스타트   : 네이티브가 window.appNavigate(path) 를 호출한다 → 역시 라우터 가드를 탄다
 */

const KEY = "afterLoginPath"

/**
 * 로그인 복귀 목적지의 유효기간.
 *
 * 문자로 들어왔다가 로그인하지 않고 앱을 방치한 뒤, 한참 지나 평소처럼 로그인했는데
 * 갑자기 갱신 결제 화면이 뜨면 사용자는 당황한다. 그래서 짧게 잡고 1회만 쓴다.
 */
const TTL = 10 * 60 * 1000

/** 복귀 목적지로 저장하면 안 되는 경로 (로그인 후 다시 로그인 화면으로 보내는 꼴이 된다) */
const NOT_RETURNABLE = ["/login", "/auth", "/sign-up"]

/**
 * 로그인 후 돌아갈 경로를 보관한다.
 * @param {String} path router 의 fullPath
 */
export function saveAfterLoginPath(path) {
    if (!isSafePath(path)) return
    if (NOT_RETURNABLE.some(p => path === p || path.startsWith(p + "?"))) return

    try {
        sessionStorage.setItem(KEY, JSON.stringify({path, exp: Date.now() + TTL}))
    } catch (e) {
        // 시크릿 모드 등으로 저장하지 못해도 로그인 자체를 막지는 않는다. 홈으로 갈 뿐이다.
    }
}

/**
 * 보관된 복귀 경로를 꺼낸다. 꺼내면 지운다(1회성).
 * @returns {String|null} 유효한 경로 또는 null
 */
export function takeAfterLoginPath() {
    try {
        const raw = sessionStorage.getItem(KEY)
        sessionStorage.removeItem(KEY)
        if (!raw) return null

        const {path, exp} = JSON.parse(raw)
        if (!exp || Date.now() >= exp) return null
        return isSafePath(path) ? path : null
    } catch (e) {
        return null
    }
}

/**
 * 앱 내부 경로인지 검사한다.
 *
 * 네이티브가 화이트리스트로 한 번 걸러 주지만 여기서도 막는다.
 * 커스텀 스킴은 아무 앱이나 호출할 수 있어서, 외부 URL 이 흘러들어오면
 * 그대로 웹뷰가 남의 페이지로 이동해 버린다.
 */
function isSafePath(path) {
    return typeof path === "string"
        && path.startsWith("/")
        && !path.startsWith("//")       // //evil.com 은 프로토콜 상대 URL 이다
}

/**
 * 네이티브 브릿지와 라우터 가드를 등록한다. main.js 에서 한 번만 호출한다.
 *
 * store / router 를 import 하지 않고 인자로 받는 이유는 순환 참조 때문이다.
 * (router → store → api → proxyHandler → router)
 *
 * @param {Object} router vue-router 인스턴스
 * @param {Object} store  vuex 스토어
 */
export function setupDeepLink(router, store) {
    // ── 네이티브 웜스타트 진입점 ──────────────────────────
    let ready = false
    let pending = null

    /**
     * 앱이 이미 떠 있을 때 네이티브가 호출한다.
     *
     * 반드시 true 를 반환해야 한다. 네이티브는 이 반환값으로
     * "브릿지가 있는 프론트인지"를 판별해, 없으면 웹뷰 전체 로드로 폴백한다.
     */
    window.appNavigate = function (path) {
        if (!isSafePath(path)) return true      // 잘못된 경로는 무시하되 폴백은 막는다

        if (!ready) {
            // 웹뷰 로드 직후에 호출되면 라우터가 아직 준비되지 않아 이동이 조용히 실패한다.
            pending = path
            return true
        }
        router.replace(path).catch(() => {})
        return true
    }

    router.isReady().then(() => {
        ready = true
        if (pending) {
            const path = pending
            pending = null
            router.replace(path).catch(() => {})
        }
    })

    // ── 인증 가드 ────────────────────────────────────────
    router.beforeEach((to) => {
        if (!to.meta || !to.meta.requiresAuth) return true

        // 토큰 만료는 여기서 보지 않는다.
        // 만료면 API 호출이 401 을 받고 proxyHandler 의 리프레시 플로우가 처리한다.
        // 가드가 만료 판정까지 하면 같은 로직이 두 벌이 된다.
        if (store.getters.getAccessToken) return true

        saveAfterLoginPath(to.fullPath)
        return "/login"
    })
}
